/**
 * Stripe's outstanding-requirement field names, said in words.
 *
 * `StripePayoutAccount.outstandingRequirements` carries Stripe's own keys —
 * `individual.verification.document`, `external_account`, `tos_acceptance.date`.
 * They are precise and unreadable, and a seller told only that "Stripe needs
 * more information" has to open Stripe to find out what, which is the one thing
 * this screen exists to save them.
 *
 * Unknown keys fall through to the generic sentence rather than being shown
 * raw: Stripe adds requirement types, and a screen that prints
 * `company.owners_provided` at a seller is worse than one that says less.
 */

type RequirementKind = 'document' | 'detail';

interface RequirementCopy {
  /** What is missing, as a noun phrase: "an identity document". */
  noun: string;
  /** Which word the heading should use. */
  kind: RequirementKind;
}

/**
 * Matched longest-prefix-first, because Stripe scopes the same requirement
 * under `individual.`, `company.` and `representative.` and the specific
 * entries have to win over the general ones.
 */
const REQUIREMENTS: [string, RequirementCopy][] = [
  ['verification.additional_document', { noun: 'another identity document', kind: 'document' }],
  ['verification.document', { noun: 'an identity document', kind: 'document' }],
  ['verification.proof_of_liveness', { noun: 'an identity check', kind: 'detail' }],
  ['external_account', { noun: 'a bank account to pay into', kind: 'detail' }],
  ['tos_acceptance', { noun: "Stripe's terms accepted", kind: 'detail' }],
  ['id_number', { noun: 'your tax or ID number', kind: 'detail' }],
  ['address', { noun: 'your address', kind: 'detail' }],
  ['dob', { noun: 'your date of birth', kind: 'detail' }],
  ['phone', { noun: 'a phone number', kind: 'detail' }],
  ['email', { noun: 'an email address', kind: 'detail' }],
  ['business_profile', { noun: 'your business details', kind: 'detail' }],
  ['business_type', { noun: 'your business type', kind: 'detail' }],
  ['owners_provided', { noun: 'your list of owners', kind: 'detail' }],
  ['company.name', { noun: 'your company name', kind: 'detail' }],
  ['first_name', { noun: 'your name', kind: 'detail' }],
  ['last_name', { noun: 'your name', kind: 'detail' }],
];

const describe = (key: string): RequirementCopy | undefined =>
  REQUIREMENTS.find(([fragment]) => key.includes(fragment))?.[1];

export interface RequirementsMessage {
  /** The heading: "Stripe needs one more document". */
  title: string;
  /** The line under it, naming what is stalled where it can. */
  body: string;
}

/** "a, b and c" — the Oxford-free list the rest of the app uses. */
const list = (parts: string[]): string =>
  parts.length <= 1
    ? parts.join('')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/**
 * What to tell the seller, from whatever Stripe happens to be waiting for.
 *
 * Falls back to the generic pair when the list is empty or holds nothing
 * recognised — true whatever Stripe wants, which is the point of a fallback.
 */
export const requirementsMessage = (keys: readonly string[]): RequirementsMessage => {
  const described = keys.map(describe).filter((copy): copy is RequirementCopy => !!copy);
  const generic = {
    title: 'Stripe needs more information',
    body: 'Payouts are on hold until Stripe has what it asked for.',
  };

  if (described.length === 0) return generic;

  // Duplicate nouns are common: first_name and last_name are both "your name".
  const nouns = Array.from(new Set(described.map((copy) => copy.noun)));
  const documents = described.filter((copy) => copy.kind === 'document').length;

  const title =
    documents === 0
      ? described.length === 1
        ? 'Stripe needs one more detail'
        : 'Stripe needs a few more details'
      : documents === 1 && described.length === 1
        ? 'Stripe needs one more document'
        : 'Stripe needs a few more documents';

  return {
    title,
    body: `Waiting on ${list(nouns)} — payouts pause until it's done.`,
  };
};
